const toFiniteNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAliasedId = (row, aliasKey, fallbackKey = "id") =>
  row?.[aliasKey] ?? row?.[fallbackKey] ?? null;

export const getTemplateRowId = (row) => getAliasedId(row, "_row_id");
export const getTemplateExampleRowId = (row) =>
  row?.template_example_id ?? row?._row_id ?? row?.id ?? null;
export const getDashboardRowId = (row) => getAliasedId(row, "_row_id");
export const getDashboardExampleRowId = (row) =>
  row?.dashboard_example_id ?? row?._row_id ?? row?.id ?? null;
export const getDashboardOrganizationId = (row) =>
  row?._organization_id ?? row?.organization_id ?? null;
export const getOrganizationRowId = (row) => getAliasedId(row, "_row_id");
export const getOrganizationParentId = (row) =>
  row?._parent_id ?? row?.parent ?? null;
export const getRoleRowId = (row) =>
  row?._role_id ?? row?.role_id ?? row?.id ?? null;
export const getUserRowId = (row) => getAliasedId(row, "_row_id");
export const getUserOrganizationId = (row) =>
  row?._organization_id ?? row?.organization_id ?? null;
export const getUserRoleId = (row) => row?._role_id ?? row?.role_id ?? null;
export const getLocationRowId = (row) => getAliasedId(row, "_row_id");
export const getLocationDeviceId = (row) =>
  row?.device_ids?.[0] ?? row?._device_id ?? row?.device_id ?? null;
export const getLocationDeviceIds = (row) =>
  Array.isArray(row?.device_ids)
    ? row.device_ids
        .map((id) => toFiniteNumberOrNull(id))
        .filter((id) => id !== null)
    : [];
export const getDeviceLocationId = (row) =>
  row?._location_id ?? row?.location_id ?? null;
export const getAuditLogRowId = (row) =>
  row?._row_id ?? row?.audit_log_id ?? row?.id ?? null;
export const getAuditLogUserId = (row) =>
  row?._user_id ?? row?.user_id ?? null;
export const getAuditLogModifiedId = (row) =>
  row?._modified_id ?? row?.modified_id ?? null;
export const getAssetCardRowId = (row) => getAliasedId(row, "_row_id");
export const getAssetCardDeviceId = (row) =>
  row?._device_id ?? row?.device_id ?? null;
export const getAssetCardNestedDeviceId = (row) =>
  row?._row_id ?? row?.id ?? null;
export const getAssetCardNestedOrganizationId = (row) =>
  row?._organization_id ?? row?.organization_id ?? null;

const normalizeHierarchy = (row) => {
  const hierarchy = Array.isArray(row?.hierarchy) ? row.hierarchy : [];
  const hierarchyIds = Array.isArray(row?._hierarchy_ids)
    ? row._hierarchy_ids.map((id) => toFiniteNumberOrNull(id))
    : [];

  return hierarchy.map((entry, index) => ({
    ...entry,
    id: hierarchyIds[index] ?? entry?.id ?? null,
  }));
};

export const normalizeTemplateListRow = (row) => ({
  ...row,
  id: getTemplateRowId(row),
});

export const normalizeTemplateExampleListRow = (row) => ({
  ...row,
  id: getTemplateExampleRowId(row),
});

export const normalizeDashboardListRow = (row) => ({
  ...row,
  id: getDashboardRowId(row),
  organization_id: getDashboardOrganizationId(row),
});

export const normalizeDashboardExampleListRow = (row) => ({
  ...row,
  id: getDashboardExampleRowId(row),
});

export const normalizeOrganizationListRow = (row) => ({
  ...row,
  id: getOrganizationRowId(row),
  parent: getOrganizationParentId(row),
  hierarchy: normalizeHierarchy(row),
});

export const normalizeRoleListRow = (row) => ({
  ...row,
  id: getRoleRowId(row),
  role_id: getRoleRowId(row),
});

export const normalizeUserListRow = (row) => ({
  ...row,
  id: getUserRowId(row),
  organization_id: getUserOrganizationId(row),
  role_id: getUserRoleId(row),
});

export const normalizeLocationListRow = (row) => ({
  ...row,
  id: getLocationRowId(row),
  device_ids: getLocationDeviceIds(row),
  device_id: getLocationDeviceId(row),
  devices: Array.isArray(row?.devices)
    ? row.devices.map((device) => ({
        ...device,
        id: device?._row_id ?? device?.id ?? null,
        did: device?._did ?? device?.did ?? null,
        location_id: getDeviceLocationId(device),
      }))
    : [],
});

export const normalizeAuditLogListRow = (row) => ({
  ...row,
  id: getAuditLogRowId(row),
  audit_log_id: getAuditLogRowId(row),
  user_id: getAuditLogUserId(row),
  modified_id: getAuditLogModifiedId(row),
});

export const normalizeAssetCardListRow = (row) => ({
  ...row,
  id: getAssetCardRowId(row),
  device_id: getAssetCardDeviceId(row),
  Device: row?.Device
    ? {
        ...row.Device,
        id: getAssetCardNestedDeviceId(row.Device),
        organization_id: getAssetCardNestedOrganizationId(row.Device),
      }
    : row?.Device,
});
